import React, { useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import toast from "react-hot-toast";
import { MdInfo, MdVisibility, MdVisibilityOff, MdWarning } from "react-icons/md";
import AppLogo from "../../components/AppLogo";
import RegistrationServices from "../../services/RegistrationServices";
import "../../styles/Login.css";

const MOBILE_RE = /^[6-9]\d{9}$/;

const ForgotPassword = () => {
  const history = useHistory();
  const regApi = useMemo(() => new RegistrationServices(), []);

  const [step, setStep] = useState(1); // 1 otp, 2 reset
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSendOtp = async () => {
    setError("");
    if (!MOBILE_RE.test(mobile)) {
      setError("Enter a valid 10 digit mobile number");
      return;
    }
    setLoading(true);
    try {
      const res = await regApi.forgotSendOtp({ mobile });
      if (res?.isSuccess) {
        setOtpSent(true);
        setDevOtp(res?.data?.devOtp || null);
        toast.success(res.message || "OTP sent");
      } else {
        setError(res?.message || "Failed to send OTP");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    if (!otp.trim()) {
      setError("Enter the OTP");
      return;
    }
    setLoading(true);
    try {
      const res = await regApi.forgotVerifyOtp({ mobile, otp: otp.trim() });
      if (res?.isSuccess) {
        setStep(2);
        toast.success(res.message || "OTP verified");
      } else {
        setError(res?.message || "Invalid OTP");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 5) {
      setError("Password must be at least 5 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const res = await regApi.forgotReset({ mobile, password });
      if (res?.isSuccess) {
        toast.success(res.message || "Password reset successfully");
        history.push("/login");
      } else {
        setError(res?.message || "Failed to reset password");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-brand">
          <AppLogo theme="light" />
        </div>
        <div className="login-header">
          <h1>Forgot Password</h1>
        </div>

        {error && (
          <div className="error-alert">
            <MdWarning className="error-icon" />
            <span className="error-message">{error}</span>
          </div>
        )}

        {step === 1 && (
          <form
            className="login-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!otpSent) handleSendOtp();
              else handleVerifyOtp();
            }}
          >
            <div className="form-group">
              <label className="form-label">
                Mobile Number <span className="required-asterisk">*</span>
              </label>
              <div className="input-wrapper">
                <input
                  className="form-input"
                  type="tel"
                  maxLength={10}
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="Registered mobile number"
                />
              </div>
            </div>

            {otpSent && (
              <div className="form-group">
                <label className="form-label">
                  OTP <span className="required-asterisk">*</span>
                </label>
                <div className="input-wrapper">
                  <input
                    className="form-input"
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="Enter 6 digit OTP"
                  />
                </div>
                {devOtp && (
                  <div className="login-notes-section" style={{ marginTop: 10 }}>
                    <div className="login-notes-content">
                      <MdInfo className="login-notes-icon" />
                      SMS not live yet — use OTP: <strong>{devOtp}</strong>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? "Please wait..." : otpSent ? "Verify OTP" : "Send OTP"}
            </button>

            {otpSent && (
              <button
                type="button"
                className="login-button"
                style={{ marginTop: 10, background: "#6b7280" }}
                disabled={loading}
                onClick={handleSendOtp}
              >
                Resend OTP
              </button>
            )}
          </form>
        )}

        {step === 2 && (
          <form className="login-form" onSubmit={handleReset}>
            <div className="form-group">
              <label className="form-label">
                New Password <span className="required-asterisk">*</span>
              </label>
              <div className="input-wrapper">
                <input
                  className="form-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 5 characters"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                Confirm Password <span className="required-asterisk">*</span>
              </label>
              <div className="input-wrapper">
                <input
                  className="form-input"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                />
              </div>
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? "Saving..." : "Reset Password"}
            </button>
          </form>
        )}

        <div className="form-footer" style={{ marginTop: 18, justifyContent: "center" }}>
          <Link to="/login" className="forgot-password-link">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
